import { useGetPlatformStats, useGetFeaturedSpaces, useGetFeaturedProducts, useListEvents, useListMembersContent } from "@workspace/api-client-react";
import ismLogo from "@assets/IMG_2117_1779819410701.png";
import ismDiamond from "@assets/IMG_3411_1779819410701.png";
import illusionistsWordmark from "@assets/IMG_4156_1779819410701.jpeg";
import skyline from "@assets/E36821C2-4283-4045-8EC6-396C979FC9A8_1779985466864.jpeg";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ExternalLink, Play, Eye, Heart, ChevronLeft, ChevronRight, CalendarDays, MapPin, Ticket } from "lucide-react";
import { useRef, useState, useCallback } from "react";
import { ContentOrientationToggle, type ContentOrientation } from "@/components/ContentOrientationToggle";
import { AdultContentToggle } from "@/components/AdultContentToggle";
import { AdultLockedLink, AdultLockOverlay } from "@/components/AdultLockedLink";

const CREATOR_SLUG: Record<number, string> = {
  7: "illusionistz",
  8: "emeraldonyx",
  9: "mistyhaze",
  10: "claudiag",
  11: "avtheentertainer",
  12: "germani",
  13: "mst",
  14: "goldie",
  18: "rhythmandsoulpatrol",
  19: "joerussketchbook",
};

export default function Home() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [contentOrientation, setContentOrientation] = useState<ContentOrientation>("day");
  const [showAdultContent, setShowAdultContent] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  const scrollBy = useCallback((dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  }, []);

  async function changeContentOrientation(next: ContentOrientation) {
    setContentOrientation(next);
  }

  const { data: stats, isLoading: statsLoading } = useGetPlatformStats();
  const { data: featuredSpaces, isLoading: spacesLoading } = useGetFeaturedSpaces();
  const { data: featuredProducts, isLoading: productsLoading } = useGetFeaturedProducts();
  const { data: events, isLoading: eventsLoading } = useListEvents({ upcoming: true });
  const { data: latestContent, isLoading: contentLoading } = useListMembersContent({ orientation: contentOrientation, showAdult: showAdultContent });

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={skyline} alt="City Skyline" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        </div>
        
        <div className="relative z-10 container px-4 flex flex-col items-center text-center space-y-6 md:space-y-8">
          <motion.img 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            src={ismLogo} 
            alt="ISM Logo" 
            className="h-20 w-20 md:h-32 md:w-32 object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.2)]" 
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="space-y-3 md:space-y-4"
          >
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold tracking-tighter uppercase font-sans text-white drop-shadow-xl">
              OWN YOUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">REALITY</span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-mono px-2">
              The premium Web3 metaverse. Explore infinite worlds, own exclusive digital assets, and live on-chain.
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 md:pt-8 w-full sm:w-auto px-4 sm:px-0"
          >
            <Link href="/worlds" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base bg-primary hover:bg-primary/80 text-white font-bold tracking-widest uppercase rounded-none border border-primary" data-testid="btn-enter-metaverse">
                ENTER THE METAVERSE
              </Button>
            </Link>
            <Link href="/marketplace" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base border-white/20 hover:bg-white/10 hover:border-white/40 tracking-widest uppercase rounded-none" data-testid="btn-explore-market">
                EXPLORE MARKET
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Ticker & Stats */}
      <section className="border-y border-border bg-black/80 backdrop-blur-md sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap md:flex-nowrap justify-between items-center gap-6 md:gap-12 overflow-x-auto no-scrollbar">
            <div className="flex-shrink-0 flex items-center gap-3">
              <span className="text-muted-foreground text-xs uppercase tracking-widest">$ISM Ticker</span>
              <span className="font-mono text-xl font-bold text-primary">
                {statsLoading ? "..." : `$${stats?.ismTokenPrice.toFixed(4)}`}
              </span>
              {stats?.ismTokenChange24h && (
                <span className={`font-mono text-sm ${stats.ismTokenChange24h >= 0 ? "text-green-500" : "text-red-500"}`}>
                  {stats.ismTokenChange24h >= 0 ? "+" : ""}{stats.ismTokenChange24h}%
                </span>
              )}
            </div>
            <div className="flex gap-12 flex-shrink-0">
              <div className="flex flex-col">
                <span className="text-muted-foreground text-[10px] uppercase tracking-widest">Active Citizens</span>
                <span className="font-mono font-bold">{statsLoading ? "..." : stats?.activeUsers.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-[10px] uppercase tracking-widest">Total Volume</span>
                <span className="font-mono font-bold">{statsLoading ? "..." : `$${stats?.totalVolumeUsd?.toLocaleString()}`}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-[10px] uppercase tracking-widest">Live Spaces</span>
                <span className="font-mono font-bold">{statsLoading ? "..." : stats?.totalSpaces.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Spaces Preview */}
      <section className="py-24 container mx-auto px-4 relative">
        <div className="absolute top-0 right-0 opacity-5 blur-[2px] pointer-events-none">
          <img src={ismDiamond} alt="Decorative" className="w-96 h-96 object-contain" />
        </div>
        
        <div className="flex justify-between items-end mb-12">
          <div className="space-y-2">
            <h2 className="text-4xl font-bold tracking-tighter uppercase">Trending Regions</h2>
            <p className="text-muted-foreground text-lg">The most active sectors right now.</p>
          </div>
          <Link href="/worlds">
            <Button variant="link" className="text-primary tracking-widest uppercase hover:text-primary/80 hidden md:flex" data-testid="link-all-worlds">
              View All Map Data →
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spacesLoading ? (
             [1,2,3,4].map(i => <div key={i} className="h-64 bg-card/50 animate-pulse rounded-xl" />)
          ) : (
            featuredSpaces?.filter(space => space.id !== 14).slice(0, 4).map(space => {
              const card = (
                <div className="group relative h-64 overflow-hidden border border-border rounded-xl cursor-pointer">
                  <img src={space.imageUrl} alt={space.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />
                  <div className="absolute bottom-0 left-0 p-6 w-full">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-primary text-xs font-mono mb-1 uppercase tracking-wider">{space.regionLabel || space.region}</p>
                        <h3 className="text-2xl font-bold uppercase">{space.name}</h3>
                      </div>
                      <div className="bg-black/60 backdrop-blur border border-white/10 px-3 py-1 rounded-full text-xs font-mono flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        {space.onlineCount}
                      </div>
                    </div>
                  </div>
                </div>
              );
              if (space.externalUrl) {
                return (
                  <a key={space.id} href={space.externalUrl} target="_blank" rel="noopener noreferrer">
                    {card}
                  </a>
                );
              }
              return <Link key={space.id} href={`/spaces/${space.id}`}>{card}</Link>;
            })
          )}

          {/* ISM New World Map — featured card */}
          <Link href="/world-map" className="group relative h-64 overflow-hidden border border-blue-500/40 hover:border-blue-400/70 rounded-xl cursor-pointer block transition-colors">
            <img src="/ism-new-world-cover.png" alt="ISM New World Map" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
            <div className="absolute bottom-0 left-0 p-6 w-full">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-blue-400 text-xs font-mono mb-1 uppercase tracking-wider">ISM Universe · World Map</p>
                  <h3 className="text-2xl font-bold uppercase group-hover:text-white transition-colors">ISM New World</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Explore the full interactive map →</p>
                </div>
                <div className="bg-black/60 backdrop-blur border border-white/10 px-3 py-1 rounded-full text-xs font-mono flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  Live
                </div>
              </div>
            </div>
          </Link>

          {/* Fashion Show Venue — hardcoded IMVU room card */}
          <a
            href="https://www.imvu.com/next/chat/room-356174852-41/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative h-64 overflow-hidden border border-pink-500/30 hover:border-pink-400/60 rounded-xl cursor-pointer block transition-colors"
          >
            <img src="/fashion-show-venue-cover.png" alt="Fashion Show Venue" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />
            <div className="absolute bottom-0 left-0 p-6 w-full">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-pink-400 text-xs font-mono mb-1 uppercase tracking-wider">ISM Universe · Fashion District</p>
                  <h3 className="text-2xl font-bold uppercase group-hover:text-white transition-colors">Fashion Show Venue</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Enter Venue on IMVU
                  </p>
                </div>
                <div className="bg-black/60 backdrop-blur border border-white/10 px-3 py-1 rounded-full text-xs font-mono flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Live on IMVU
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Latest Creator Posts */}
      <section className="py-16">
        <div className="container mx-auto px-4 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-end mb-8">
          <div className="space-y-1">
            <p className="text-primary text-xs font-mono uppercase tracking-widest mb-1">Creator Content</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase">Latest Posts</h2>
            <p className="text-muted-foreground text-sm md:text-base">Fresh drops from our creators.</p>
          </div>
          <div className="flex items-center gap-3">
            <ContentOrientationToggle value={contentOrientation} onChange={changeContentOrientation} />
            <AdultContentToggle value={showAdultContent} onChange={setShowAdultContent} />
            <Link href="/content">
              <Button variant="link" className="text-primary tracking-widest uppercase hover:text-primary/80 hidden md:flex">
                See All →
              </Button>
            </Link>
          </div>
        </div>

        {/* Horizontal scroll row */}
        <div className="relative">
          {/* Left arrow */}
          <button
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className={`absolute left-2 top-[calc(50%-2rem)] z-10 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur-md flex items-center justify-center text-white shadow-xl transition-all duration-300 hover:bg-primary/80 hover:border-primary hover:scale-110 ${canScrollLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Right arrow */}
          <button
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className={`absolute right-2 top-[calc(50%-2rem)] z-10 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur-md flex items-center justify-center text-white shadow-xl transition-all duration-300 hover:bg-primary/80 hover:border-primary hover:scale-110 ${canScrollRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Left fade edge */}
          <div className={`absolute left-0 top-0 bottom-4 w-16 bg-gradient-to-r from-background to-transparent pointer-events-none z-[5] transition-opacity duration-300 ${canScrollLeft ? "opacity-100" : "opacity-0"}`} />
          {/* Right fade edge */}
          <div className={`absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-background to-transparent pointer-events-none z-[5] transition-opacity duration-300 ${canScrollRight ? "opacity-100" : "opacity-0"}`} />

        <div ref={scrollRef} onScroll={updateArrows} className="pl-4 md:pl-[max(1rem,calc((100vw-1280px)/2+1rem))] overflow-x-auto no-scrollbar">
          <div className="flex gap-3 md:gap-4 pb-4" style={{ width: "max-content" }}>
            {contentLoading
              ? Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="flex-none w-52 md:w-72 space-y-2">
                    <div className="w-full aspect-video bg-card/50 animate-pulse rounded-xl" />
                    <div className="h-3 bg-card/50 animate-pulse rounded w-4/5" />
                    <div className="h-3 bg-card/50 animate-pulse rounded w-2/5" />
                  </div>
                ))
              : latestContent?.slice(0, 10).map((post) => (
                  <AdultLockedLink key={post.id} item={post} href={`/creators/${CREATOR_SLUG[post.creatorId] ?? post.creatorId}/post/${post.id}`}>
                    <div className="group flex-none w-52 md:w-72 cursor-pointer">
                      {/* Thumbnail */}
                      <div className="relative w-full aspect-video overflow-hidden rounded-xl border border-border/30 group-hover:border-primary/50 transition-colors duration-300 mb-3">
                        <img
                          src={post.thumbnailUrl ?? ""}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Dark overlay */}
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors duration-300" />
                        {post.isAdult && <AdultLockOverlay />}
                        {/* Play button */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 border border-white/30 backdrop-blur-sm flex items-center justify-center group-hover:bg-primary/80 group-hover:border-primary transition-all duration-300 group-hover:scale-110">
                            <Play className="w-4 h-4 md:w-5 md:h-5 text-white fill-white ml-0.5" />
                          </div>
                        </div>
                        {/* Stats strip */}
                        <div className="absolute bottom-2 left-2 right-2 flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="flex items-center gap-1 text-white/80 text-[10px] font-mono">
                            <Eye className="w-3 h-3" />{(post.viewCount ?? 0).toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1 text-white/80 text-[10px] font-mono">
                            <Heart className="w-3 h-3" />{(post.likeCount ?? 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {/* Info below card */}
                      <div className="flex items-start gap-2 px-0.5">
                        <img
                          src={post.creatorAvatar ?? ""}
                          alt={post.creatorName}
                          className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover flex-none mt-0.5 border border-border/40"
                        />
                        <div className="min-w-0">
                          <p className="text-white text-xs md:text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">{post.title}</p>
                          <p className="text-muted-foreground text-[10px] md:text-xs mt-0.5 truncate">{post.creatorName}</p>
                        </div>
                      </div>
                    </div>
                  </AdultLockedLink>
                ))}
          </div>
        </div>
        </div>{/* end .relative wrapper */}

        <div className="mt-4 flex justify-center md:hidden px-4">
          <Link href="/creators">
            <Button variant="outline" size="sm" className="w-full tracking-widest uppercase border-primary/40 text-primary hover:bg-primary/10">
              See All Content →
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Upcoming Events */}
      <section className="py-16 bg-black/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-8">
            <div className="space-y-1">
              <p className="text-primary text-xs font-mono uppercase tracking-widest mb-1">Live Events</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase">Upcoming Events</h2>
              <p className="text-muted-foreground text-sm">Real experiences. Real cities. Real culture.</p>
            </div>
            <Link href="/events">
              <Button variant="link" className="text-primary tracking-widest uppercase hover:text-primary/80 hidden md:flex">
                All Events →
              </Button>
            </Link>
          </div>
          {eventsLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-72 w-64 flex-shrink-0 bg-card/50 animate-pulse rounded-xl" />)}
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {[19, 18, 15, 7, 8, 13, 12, 14, 17].map(id => events?.find(e => e.id === id)).filter(Boolean).map(event => (
                <Link key={event!.id} href={`/events/${event!.id}`}>
                  <div className="group relative h-72 w-64 flex-shrink-0 snap-start overflow-hidden border border-border hover:border-primary/40 rounded-xl cursor-pointer transition-colors">
                    {event!.imageUrl ? (
                      <img src={event!.imageUrl} alt={event!.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full bg-card" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-primary text-white px-2.5 py-1 rounded-full">
                        <CalendarDays className="w-2.5 h-2.5" />
                        {new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" }).format(new Date(event!.startTime))}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-primary text-[10px] font-mono uppercase tracking-widest mb-1">{event!.category}</p>
                      <h3 className="font-bold text-base uppercase leading-tight line-clamp-2 mb-1">{event!.title}</h3>
                      {event!.spaceName && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{event!.spaceName}
                        </p>
                      )}
                      <div className="mt-3">
                        <Button size="sm" className="w-full gap-1.5 text-xs bg-primary/90 hover:bg-primary">
                          <Ticket className="w-3 h-3" />
                          View Event &amp; Tickets
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Powered By Banner */}
      <section className="py-16 bg-card border-y border-border">
        <Link href="/illusionists">
          <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-center gap-8 text-center opacity-70 hover:opacity-100 transition-opacity cursor-pointer group">
            <span className="text-sm font-mono tracking-widest uppercase text-muted-foreground group-hover:text-primary transition-colors">POWERED BY</span>
            <img src={illusionistsWordmark} alt="The Illusionists" className="h-16 object-contain mix-blend-screen" />
          </div>
        </Link>
      </section>
    </div>
  );
}
